import { Component, OnInit, AfterViewInit, ElementRef } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { ReleaseMgmtService } from '@app/shared/services/releaseMgmtService';
import { ServicesListDto } from '@app/shared/model/servicesListDto';
import { LoaderService } from '@app/shared/services/loader.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { ReleaseCreationService } from '@app/shared/services/releaseCreationService';
import { QuillModule } from 'ngx-quill';
import { TriggerReleaseDto } from '@app/shared/model/triggerReleaseDto';
import { MatSelectModule } from '@angular/material/select';
import { NgSelectWithSelectAllComponent } from '@app/shared/shared-components/ng-select-with-select-all/ng-select-with-select-all.component';
import { PageHeaderComponent } from '@app/shared/page-header/page-header.component';
import { CommonModule } from '@angular/common';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatIcon } from '@angular/material/icon';
import { AlertService } from '@app/shared/services/alert.service';

@Component({
    standalone: true,
    selector: 'app-trigger-release',
    templateUrl: './trigger-release.component.html',
    styleUrls: ['./trigger-release.component.scss'],
    imports: [CommonModule, QuillModule, FormsModule, ReactiveFormsModule, MatSelectModule, NgSelectWithSelectAllComponent, PageHeaderComponent, MatCheckbox, MatIcon]
})
export class TriggerReleaseComponent implements OnInit, AfterViewInit {
  // Declaration of an array to store ServicesListDto objects
  servicesList: ServicesListDto[] = [];
  // Declaration of an empty array to store environments
  environments = [];
  // Declaration of an empty array to store filtered environments
  filteredEnvironments = [];
  // Declaration of a string variable to store the release name
  releaseName = '';
  // Declaration of a string variable to store the selected environment Id
  selectedEnvironmentId = null;
  // Declaration of a string variable to store the page title
  pageTitle = 'Trigger Release Deployment';
  // Declaration of a string variable to store the page subtitle
  pageSubTitle = 'Select the necessary services and environment tailored to your deployment needs.';
  // Declaration of an UntypedFormControl object for search filter control environments
  searchFilterControlEnvironments: UntypedFormControl = new UntypedFormControl();
  // Declaration of a number variable to store the release ID
  releaseId = 0;
  // Declaration of a string variable to store the select environment info message
  SelectEnvInfoMsg = 'Select an Environment';
  SelectServicesInfoMsg = 'Select Services*';
  DeploymentPurposeInfoMsg = 'Please mention the purpose for deployment';
  pageTitleTooltip = 'Services can be deployed to a single region at a time';
  selectedServices = [];
  deploymentPurpose = '';
  customQuillOptions = [
    { import: 'attributors/style/font', whitelist: ['Arial', 'Times New Roman', 'Calibri', 'Comic Sans MS'] },
    { import: 'attributors/style/size', whitelist: ['10px', '12px', '14px', '16px', '18px', '24px', '36px'] }
  ];
  quillModules = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike',
       { 'script': 'sub' }, { 'script': 'super' },
       { 'align': [] },
       { 'indent': '-1' }, { 'indent': '+1' },
       { 'list': 'ordered' }, { 'list': 'bullet' },
       { 'header': [1, 2, 3, 4, 5, 6, false] },
       { 'font': ['Arial', 'Times New Roman', 'Calibri', 'Comic Sans MS'] }],
      [{ 'size': ['10px', '12px', '14px', '16px', '18px', '24px', '36px'] },
       { 'color': [] }, { 'background': [] },
       'clean', 'code-block']
    ]
  };
  quillStyles = {
    height: '25rem',
    'min-height': '15rem',
    'font-family': 'EYInterstate'
  };
  InfoForService: any;
  isDBSyncInitiated = false;
  ShowStagingChkbox: boolean;

  // flag to check if given release is latest release
  isLatestRelease : boolean;

  // Constructor for the TriggerReleaseComponent class, injecting ReleaseMgmtService and LoaderService
  constructor(
    public releaseMgmtService: ReleaseMgmtService,
    public loader: LoaderService,
    public route: ActivatedRoute,
    public toastr: ToastrService,
    private readonly router: Router,
    public releaseCreationService: ReleaseCreationService,
  public alertService: AlertService,
  private el: ElementRef) { }

  onQuillEditorCreated(editor: any): void {
    const Q = editor.constructor as any;
    const sizeStyle = Q.import('attributors/style/size');
    if (sizeStyle) {
      Q.register({ 'formats/size': sizeStyle }, true);
    }
    const fontStyle = Q.import('attributors/style/font');
    if (fontStyle) {
      Q.register({ 'formats/font': fontStyle }, true);
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      const buttonTooltips: Record<string, string> = {
        'button.ql-bold': 'Bold', 'button.ql-italic': 'Italic', 'button.ql-underline': 'Underline',
        'button.ql-strike': 'Strikethrough', 'button.ql-script[value="sub"]': 'Subscript',
        'button.ql-script[value="super"]': 'Superscript', 'button.ql-indent[value="-1"]': 'Decrease Indent',
        'button.ql-indent[value="+1"]': 'Increase Indent', 'button.ql-list[value="ordered"]': 'Ordered List',
        'button.ql-list[value="bullet"]': 'Unordered List', 'button.ql-clean': 'Remove Formatting',
        'button.ql-code-block': 'Code Block'
      };
      const pickerTooltips: Record<string, string> = {
        '.ql-align': 'Text Alignment', '.ql-header': 'Heading', '.ql-font': 'Font Family',
        '.ql-size': 'Font Size', '.ql-color': 'Text Color', '.ql-background': 'Background Color'
      };
      const toolbar = this.el.nativeElement.querySelector('.ql-toolbar');
      if (toolbar) {
        Object.entries(buttonTooltips).forEach(([selector, title]) => {
          const el = toolbar.querySelector(selector);
          if (el) el.setAttribute('title', title);
        });
        Object.entries(pickerTooltips).forEach(([selector, title]) => {
          const picker = toolbar.querySelector(`.ql-picker${selector}`);
          if (picker) {
            const label = picker.querySelector('.ql-picker-label');
            if (label) label.setAttribute('title', title);
          }
        });
      }
    }, 500);
  }

  ngOnInit(): void {
    // Call the getServicesAndEnvironments() method to fetch services and environments data
    this.getEnvironments();

    // Subscribe to changes in the searchFilterControlEnvironments value
    this.searchFilterControlEnvironments.valueChanges.subscribe((value) => {
      // Filter the environments based on the search value
      this.filteredEnvironments = this.environments.filter((item) => item.environmentName.toLowerCase().includes(value.toLowerCase()));
    });
  }

  isStaging(envId: any): boolean {
    const env = this.filteredEnvironments.find(e => e.environmentId === envId);
    return env ? env.isStaging : false;
  }

  onEnvironmentChange(selectedEnvId: any) {
    if (selectedEnvId) {
      this.ShowStagingChkbox = this.isStaging(selectedEnvId);
    }
    this.ResetServiceList(selectedEnvId);
  }

  ResetServiceList(envId: string) {
    this.servicesList = [];
    this.selectedServices = [];
    if (envId) {
      this.loader.showSpinner.next(true);
      this.releaseMgmtService.apiGetServiceListByReleaseAndEnvironment(this.releaseId, envId).subscribe({
        next: (res) => {
          this.loader.showSpinner.next(false);
          if (res) {
            this.servicesList = res.map((service) => ({
              serviceName: service.serviceName,
              branchName: service.branchName ? service.branchName : res.releasePrefix + '-' + service.versionType + '-' + service.versionNumber,
              versionType: service.versionType,
              serviceGroup: service.serviceGroup,
            }));
          }
        },
        error: (err) => {
          this.loader.showSpinner.next(false);
          this.toastr.error('Error while fetching services', 'Error');
        }
      });
    }

  }

  triggerRelease() {
    // Show the spinner/loader
    this.loader.showSpinner.next(true);
    // Check if the selectedEnvironmentId is null, 0, or undefined
    if (!this.selectedEnvironmentId) {
      // Display an error message if the selected environment is not valid
      this.toastr.error('Please select an environment to trigger release deployment', 'Error');
      // Hide the spinner/loader
      this.loader.showSpinner.next(false);
      return;
    }
    const triggerReleaseDto = {
      releaseId: this.releaseId,
      envMappingId: this.selectedEnvironmentId,
      isDBSyncInitiated: this.isDBSyncInitiated,
      serviceNames: this.selectedServices,
      deploymentPurpose: this.deploymentPurpose
    } as TriggerReleaseDto;
    // Call the apiPostCreateReleasePipeline method of the releaseCreationService
    this.releaseCreationService.apiPostCreateReleasePipeline(triggerReleaseDto).subscribe({
      next: (res) => {
        if (res) { this.toastr.success('Deployment process has initiated.', 'Success'); this.router.navigate(['release']); }
        else {
          // Display an error message if there is an error while triggering release deployment
          this.toastr.error('Error while triggering release deployment.', 'Error'); this.loader.showSpinner.next(false);
        }
      },
      error: (err) => {
        // Display an error message if there is an error while triggering release deployment
        this.toastr.error('Error while triggering release deployment.', 'Error');
        // Hide the spinner/loader
        this.loader.showSpinner.next(false);
      },
      complete: () => { this.loader.showSpinner.next(false); }
    });
  }

  getEnvironments() {
    // Show the spinner/loader
    this.loader.showSpinner.next(true);
    // Get the releaseId from the route parameter
    this.releaseId = Number(this.route.snapshot.paramMap.get('releaseId'));
    // Call the API to get release data by releaseId
    this.releaseMgmtService.apiGetReleaseDataById(this.releaseId).subscribe({
      next: (res) => {
        if (res) {
          // Extract release name from the response
          this.releaseName = res.releaseName;
          // Set the filtered environments data from the response
          this.filteredEnvironments = res.environmentsConfigMapping;
          this.environments = res.environmentsConfigMapping;
          this.isLatestRelease = res.isLatestRelease;
        }

        // if given release is not the latest release warm the user
        if (!this.isLatestRelease) {
          this.alertService.buildDialog().fire({
          title: 'Do you want to continue?',
          text: 'A subsequent release has already been created; would you still like to proceed with deploying services in this release?',
          icon: this.alertService.WarningIcon,
          showCancelButton: true,
          confirmButtonText: 'Yes',
          cancelButtonText: 'Cancel',
        }).then((result) => {
          if (!result.isConfirmed) {
            this.router.navigate(['release']);
          }
        });
      }

      },
      error: (err) => { this.toastr.error('Error while fetching release data', 'Error'); },
      complete: () => {
        // Hide the spinner/loader
        this.loader.showSpinner.next(false);
      }
    });
  }

  cancel() {
    this.router.navigate(['release']);
  }

  requiredValidationForWYSIWYG(): boolean {
    return this.deploymentPurpose !== null && this.deploymentPurpose !== undefined && this.isEditorContentNotEmpty(this.deploymentPurpose);
  }
  isEditorContentNotEmpty(editorElement) {
    const html = editorElement;

    // Remove HTML tags and decode entities/whitespace
    const text = html
      .replaceAll(/<[^>]*>/g, '') //NOSONAR      // Remove tags
      .replaceAll('&nbsp;', '')        // Remove non-breaking spaces
      .replaceAll(/\s+/g, '')           // Remove all whitespace

    return text.length > 0;
  }
} 
